import { memo, useCallback, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormBuilderCardWrapper } from '../../../FormBuilderCardWrapper';
import { FormBuilderElement } from '../../../FormBuilderElement';
import {
  useFormBuilderFields,
  useFormBuilderFieldWithError,
} from '../../../hooks';
import {
  EFormBuilderFieldType,
  TFormBuilderMultipleChoiceField,
} from '@/types';

type TProps = {
  field: TFormBuilderMultipleChoiceField;
  index: number;
};

export const FormBuilderMultipleChoice =
  /**
   * Multiple choice field component for form builder with checkboxes, editable label and options
   * @param {TProps} props - Component props
   * @param {TFormBuilderMultipleChoiceField} props.field - Multiple choice field data
   * @param {number} props.index - Field index in the form
   * @returns {JSX.Element} Form builder multiple choice field component
   */
  memo(({ field, index }: TProps) => {
    const { t } = useTranslation();
    const labelId = useId();
    const [active, setActive] = useState(false);
    const onActiveChange = useCallback(
      (value: boolean) => setActive(value),
      [],
    );

    const { changeFields, removeField, copyField, moveDownField, moveUpField } =
      useFormBuilderFields(field);

    const changeMultipleChoiceFields = useCallback(
      (updated: Partial<TFormBuilderMultipleChoiceField>) => {
        changeFields({ ...field, ...updated });
      },
      [changeFields, field],
    );

    const error =
      useFormBuilderFieldWithError<TFormBuilderMultipleChoiceField>(index);

    const optionsWithError = field.options.map((option, index) => ({
      ...option,
      error: !!error?.options?.[index],
    }));

    return (
      <FormBuilderCardWrapper
        variant={EFormBuilderFieldType.MULTIPLE_CHOICE}
        active={active}
        onActiveChange={onActiveChange}
        onRemove={removeField}
        onDownMove={moveDownField}
        onUpMove={moveUpField}
        onDuplicate={copyField}
        error={!!error}
      >
        <FormBuilderElement
          alert={t('form-builder.multiple-choice-alert')}
          labelId={labelId}
          label={
            <FormBuilderElement.Label
              label={field.label}
              id={labelId}
              onLabelChange={(label) => changeMultipleChoiceFields({ label })}
              placeholder={t('form-builder.your-label-here')}
              required={field.required}
              error={!!error?.label}
            />
          }
          required={field.required}
          onRequiredChange={(required) =>
            changeMultipleChoiceFields({ required })
          }
          options={
            <FormBuilderElement.Options
              options={optionsWithError}
              onChange={(options) => changeMultipleChoiceFields({ options })}
              variant="checkbox"
            />
          }
        />
      </FormBuilderCardWrapper>
    );
  });

FormBuilderMultipleChoice.displayName = 'FormBuilderMultipleChoice';
